import 'components/DSYS/editor/dynamicNode/registerDynamicNodes';

import {IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PuzzlePieceLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {analyticsLogger} from 'analytics/analyticsLogger';
import cx from 'classnames';
import {
  getDynamicNodeDef,
  listRegisteredDynamicNodeDefs,
} from 'components/DSYS/editor/dynamicNode/registry';
import type {DynamicNodeDefinition} from 'components/DSYS/editor/dynamicNode/types';
import {$createDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import styles from 'components/DSYS/editor/plugins/SelectedTextToolbar.module.css';
import {t} from 'i18next';
import {$createParagraphNode, $getSelection, $insertNodes, $isRangeSelection} from 'lexical';
import {useMemo} from 'react';

function groupDynamicNodeDefs(defs: DynamicNodeDefinition[]): Array<{
  categoryKey: string | undefined;
  defs: DynamicNodeDefinition[];
}> {
  const byCategory = new Map<string | undefined, DynamicNodeDefinition[]>();
  for (const def of defs) {
    const key = def.categoryKey;
    const bucket = byCategory.get(key) ?? [];
    bucket.push(def);
    byCategory.set(key, bucket);
  }
  for (const bucket of byCategory.values()) {
    bucket.sort((a, b) => a.kindId.localeCompare(b.kindId));
  }
  const entries = Array.from(byCategory.entries());
  entries.sort(([a], [b]) => {
    if (a === b) return 0;
    if (a === undefined) return 1;
    if (b === undefined) return -1;
    return a.localeCompare(b);
  });
  return entries.map(([categoryKey, grouped]) => ({categoryKey, defs: grouped}));
}

export const DynamicNodeInsertToolbarButton = () => {
  const [editor] = useLexicalComposerContext();

  const segments = useMemo(() => groupDynamicNodeDefs(listRegisteredDynamicNodeDefs()), []);

  const insertKind = (kindId: string) => {
    const def = getDynamicNodeDef(kindId);
    if (!def) return;
    analyticsLogger().logEvent('EDITOR_TOOLBAR_BUTTON_CLICKED', {
      type: `dynamic_node_insert:${kindId}`,
    });
    editor.update(() => {
      const node = $createDynamicNode(kindId, def.defaultBinding, undefined);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $insertNodes([node]);
      } else {
        const p = $createParagraphNode();
        p.append(node);
        $insertNodes([p]);
      }
    });
  };

  const title = t('dynamic_node_insert_menu');

  return (
    <Menu.Wrapper
      onSelection={(selectionId) => {
        if (typeof selectionId === 'string' && selectionId) {
          insertKind(selectionId);
        }
      }}
      shouldPropagateClickOutsideMouseEvents
    >
      {({getContentProps, getTriggerProps}) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            <Box {...triggerProps}>
              <Tooltip title="Add dynamic template node">
                <IconButton
                  className={cx(styles.button, triggerProps.className)}
                  variant="borderless"
                  aria-label={title}
                  title={title}
                >
                  <UIIcon src={PuzzlePieceLine} className={atoms({color: 'Text Base'})} />
                </IconButton>
              </Tooltip>
            </Box>
            <Menu.Content {...getContentProps()} minWidth="240px" placement="bottom-start">
              {segments.map(({categoryKey, defs}) => (
                <Menu.Segment
                  key={categoryKey ?? '__general__'}
                  withLabel={categoryKey ? t(categoryKey) : t('dynamic_node_category_general')}
                >
                  {defs.map((def) => (
                    <Menu.ActionItem key={def.kindId} value={def.kindId}>
                      {t(def.labelKey)}
                    </Menu.ActionItem>
                  ))}
                </Menu.Segment>
              ))}
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
